import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { expenseReportQueries } from 'src/services/expense-reports/queries'
import { SelectOption } from 'src/services/base-model'
import { CustomSelect } from 'src/components/custom-select'

interface ReportOverrideProps {
  suggestedReportId?: string | null
  suggestedReportName?: string | null
  onChange: (reportId: string | null) => void
  disabled?: boolean
}

/**
 * Finish-step control: shows the auto-detected report for the statement's new
 * expenses and lets the user override it. Read-only when the statement is
 * confirmed — the name is shown as static text instead of the picker.
 */
export const ReportOverride = ({
  suggestedReportId,
  suggestedReportName,
  onChange,
  disabled = false,
}: ReportOverrideProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { data: reports } = useQuery({
    ...expenseReportQueries.list({ filters: { search } }),
    enabled: !disabled,
  })

  const options = useMemo<SelectOption[]>(
    () =>
      reports?.results.map((r) => ({
        label: r.organizationRef?.name ? `${r.name} - ${r.organizationRef.name}` : r.name,
        value: r.id,
      })) ?? [],
    [reports?.results],
  )

  const values = useMemo<SelectOption[]>(() => {
    if (!suggestedReportId) return []
    return [{ label: suggestedReportName ?? suggestedReportId, value: suggestedReportId }]
  }, [suggestedReportId, suggestedReportName])

  return (
    <div data-testid="report-override">
      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-text-tertiary">
        {t('statementReview.suggestedReportLabel')}
      </p>
      {disabled ? (
        <p className="text-sm text-text-secondary" data-testid="report-override-readonly">
          {suggestedReportName ?? t('statementReview.noReport')}
        </p>
      ) : (
        <CustomSelect
          searchable
          clearable
          options={options}
          values={values}
          placeholder={t('statementReview.overrideReportPlaceholder')}
          searchFn={({ state }) => {
            setSearch(state.search)
            return options
          }}
          onChange={(vals) => onChange(vals[0]?.value ? String(vals[0].value) : null)}
        />
      )}
    </div>
  )
}
